import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tags, ArrowUp, ArrowDown, Package } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { photoUrl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Card, Chip, Button, Skeleton, EmptyState, Toast } from "@/ui";

export default function Categories() {
  const { shopId, can } = useAuth();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const canEdit = !!can?.add_product_change_price;

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/categories${shopId ? `?shopId=${shopId}` : ""}`);
      setCats(r.data.categories || []);
    } catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, [shopId]);
  useEffect(() => { if (!shopId) { navigate("/pick-shop", { replace: true }); return; } load(); }, [shopId, navigate, load]);

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= cats.length) return;
    const next = cats.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setCats(next);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/categories/order", { shopId, order: cats.map((c) => c.name) });
      setDirty(false);
      setToast("order saved");
      setTimeout(() => setToast(""), 2500);
    } catch (e) { setError(friendlyError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="row-between" style={{ marginBottom: 4, flexWrap: "wrap", gap: 8 }}>
        <h1 className="t-h1">categories</h1>
        {canEdit && dirty ? (
          <Button variant="primary" data-testid="save-order-btn" loading={saving} onClick={save}>save order</Button>
        ) : null}
      </div>
      <p className="muted" style={{ marginBottom: 24 }}>group your items and put the groups in the order you want them to show.</p>
      {error ? <div className="err-text" data-testid="categories-error" style={{ marginBottom: 12 }}>{error}</div> : null}

      {loading ? (
        <div className="stack"><Skeleton variant="card" /><Skeleton variant="card" /></div>
      ) : cats.length === 0 ? (
        <EmptyState icon={<Tags size={20} strokeWidth={1.75} />} title="no categories yet — add a category to an item to see it here" />
      ) : (
        <div className="stack" style={{ gap: 12 }} data-testid="categories-list">
          {cats.map((c, i) => (
            <Card key={c.name} data-testid={`category-${c.name}`}>
              <div className="row-between" style={{ marginBottom: 10 }}>
                <div className="row" style={{ gap: 8 }}>
                  <Tags size={18} strokeWidth={1.75} className="text-slate-400" />
                  <span className="t-h3">{c.name}</span>
                  <Chip>{c.count}</Chip>
                </div>
                {canEdit ? (
                  <div className="row" style={{ gap: 4 }}>
                    <Button size="sm" data-testid={`cat-up-${c.name}`} disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp size={15} /></Button>
                    <Button size="sm" data-testid={`cat-down-${c.name}`} disabled={i === cats.length - 1} onClick={() => move(i, 1)}><ArrowDown size={15} /></Button>
                  </div>
                ) : null}
              </div>
              <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                {c.items.map((it) => (
                  <button key={it.id} className="row" data-testid={`cat-item-${it.id}`}
                    onClick={() => navigate(`/products/${it.id}`)}
                    style={{ gap: 8, padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 10, background: "var(--surface)", cursor: "pointer" }}>
                    {it.photos?.[0] ? <img src={photoUrl(it.photos[0])} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: "cover" }} />
                      : <Package size={16} style={{ opacity: 0.4 }} />}
                    <span className="t-small">{it.name}</span>
                    {it.soldOut ? <Chip tone="bad">sold out</Chip> : null}
                  </button>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="categories-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}
